import { ValueLine } from "@components/brand/ValueLine";
import { Page } from "@components/layout/Page";
import { FormBlock } from "@components/product/Blocks";
import { Section, SectionHeading } from "@components/shared/Section";
import { PartnerLogo } from "@components/brand/PartnerLogo";
import { PARTNERS } from "@/lib/partners";
import { FLEX } from "@/lib/site";
import { useTranslations } from "@services/strings";
import { Building2, CalendarClock, ShieldCheck, Wallet } from "lucide-react";
import { GetStaticProps } from "next";
import { useRouter } from "next/router";

/**
 * Landing for developer partnerships. Kept out of the retail navigation
 * (homeowners never need it) and reached from the footer or shared directly
 * with prospects.
 */
const DevelopersPage = () => {
  const { t } = useTranslations();
  const { locale } = useRouter();
  const lang = locale === "ar-EG" ? "ar" : "en";

  const icons = {
    liquidity: Wallet,
    planner: CalendarClock,
    homeowners: Building2,
    compliance: ShieldCheck,
  } as const;

  return (
    <Page
      title={t("site.developers.meta.title")}
      description={t("site.developers.meta.description")}
      headerTone="navy"
    >
      <section className="relative overflow-hidden bg-navy text-white">
        <ValueLine className="pointer-events-none absolute inset-x-0 bottom-0 h-1/2 w-full opacity-60 rtl:-scale-x-100" />
        <div className="container relative py-16 md:py-28">
          <p className="text-sm font-semibold text-mint">
            {t("site.developers.hero.eyebrow")}
          </p>
          <h1 className="display mt-3 max-w-4xl text-display-sm md:text-display">
            {t("site.developers.hero.title")}
          </h1>
          <p className="mt-6 max-w-prose text-body text-slate-300 md:text-lg md:leading-relaxed">
            {t("site.developers.hero.subtitle")}
          </p>
          <a
            href="#contact-form"
            className="mt-8 inline-flex h-12 items-center rounded-full bg-mint px-6 text-base font-semibold text-ink hover:bg-mint-soft"
          >
            {t("site.common.talkToUs")}
          </a>
        </div>
      </section>

      <Section tone="white">
        <SectionHeading title={t("site.developers.offer.title")} />
        <dl className="mt-12 grid gap-x-12 gap-y-10 md:grid-cols-2">
          {(["liquidity", "planner", "homeowners", "compliance"] as const).map(
            (id) => {
              const Icon = icons[id];
              return (
                <div key={id} className="flex gap-5">
                  <span className="mt-0.5 grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-mint-soft text-mint-ink">
                    <Icon size={20} aria-hidden="true" />
                  </span>
                  <div>
                    <dt className="text-h3 font-semibold">
                      {t(`site.developers.offer.items.${id}.title`)}
                    </dt>
                    <dd className="mt-2 max-w-prose text-body text-slate-600">
                      {t(`site.developers.offer.items.${id}.body`)}
                    </dd>
                  </div>
                </div>
              );
            },
          )}
        </dl>
      </Section>

      <Section tone="mist" tight>
        <div className="grid gap-6 md:grid-cols-[0.8fr_1.2fr] md:gap-16">
          <SectionHeading
            title={t("site.developers.partners.title")}
            lead={t("site.developers.partners.body")}
          />
          <ul className="flex flex-wrap gap-3 self-start">
            {PARTNERS.map((partner) => (
              <li
                key={partner.slug}
                className="flex items-center gap-4 rounded-2xl border border-slate-200 bg-white py-3 pe-5 ps-4"
              >
                <span className="grid h-6 w-36 shrink-0 place-items-center">
                  <PartnerLogo
                    partner={partner}
                    lang={lang}
                    className="h-auto max-h-6 w-auto max-w-full"
                  />
                </span>
                <span className="whitespace-nowrap text-sm text-slate-500">
                  {partner.compounds[lang].join(" · ")}
                </span>
              </li>
            ))}
          </ul>
        </div>
      </Section>

      <FormBlock
        tone="white"
        title={t("site.developers.form.title")}
        body={t("site.developers.form.body")}
        interest="developer"
        placement="developers"
      />
    </Page>
  );
};

export const getStaticProps: GetStaticProps = async () =>
  FLEX.live ? { props: {} } : { notFound: true };

export default DevelopersPage;
